// What an expense's Status says — one rule for the Expenses table and the
// expense window.
//
// "Reimbursed" means the money came back, either way it can:
//   * the CLIENT paid it — it's on an invoice that is now paid in full; or
//   * someone clicked Reimburse (`reimbursed_at`, e.g. paying back an employee).
// It is the end state, so it stands alone; the tooltip says how it got there.
// Before that, a billed expense reads "Billed" (on an unpaid invoice), and an
// unbilled one shows Reimbursable / Approved as before.

import { fmtDate } from './date';

export interface ExpenseStatusPill {
  label: 'Reimbursed' | 'Billed' | 'Reimbursable' | 'Approved';
  tone: 'success' | 'neutral' | 'warn' | 'cyan';
  title?: string;
}

export interface ExpenseStatusInput {
  reimbursable: boolean;
  approved_at: number | null;
  reimbursed_at: number | null;
  billed?: boolean;
  billed_invoice_number?: string | null;
  billed_invoice_status?: string | null;
  billed_invoice_paid_at?: number | null;
}

export function expenseStatusPills(e: ExpenseStatusInput): ExpenseStatusPill[] {
  const inv = e.billed_invoice_number ?? null;
  const clientPaid = inv !== null && e.billed_invoice_status === 'paid';

  if (clientPaid || e.reimbursed_at !== null) {
    const how = clientPaid
      ? `billed on ${inv}, paid${e.billed_invoice_paid_at ? ` ${fmtDate(Number(e.billed_invoice_paid_at))}` : ''}`
      : `reimbursed ${fmtDate(Number(e.reimbursed_at))}`;
    return [{ label: 'Reimbursed', tone: 'success', title: `Reimbursable and approved — ${how}` }];
  }
  if (inv !== null || e.billed) {
    return [{
      label: 'Billed',
      tone: 'neutral',
      title: inv ? `On ${inv} — not paid yet` : 'On an invoice — not paid yet',
    }];
  }
  const out: ExpenseStatusPill[] = [];
  if (e.reimbursable) out.push({ label: 'Reimbursable', tone: 'warn' });
  if (e.approved_at !== null) out.push({ label: 'Approved', tone: 'cyan' });
  return out;
}

/** Sort rank for the Status column: nothing < reimbursable < approved < billed < reimbursed. */
export function expenseStatusRank(e: ExpenseStatusInput): number {
  const first = expenseStatusPills(e)[0]?.label;
  if (first === 'Reimbursed') return 4;
  if (first === 'Billed') return 3;
  if (e.approved_at !== null) return 2;
  if (e.reimbursable) return 1;
  return 0;
}
